import { difference } from "lodash";
import { DateTime } from "luxon";

import toast from "components/ui/toast/Toast";
import { ReferenceOrganisation_Address } from "./generated/app-service-gql/graphql";

const NUMBER_FORMAT = new Intl.NumberFormat(undefined);

const NUMBER_FORMAT_COMPACT = new Intl.NumberFormat(undefined, {
  notation: "compact",
  maximumFractionDigits: 1,
});

export const EM_DASH = "–";

export const EMPTY_ARRAY = [];

const OPEN_ENDED_DATE = DateTime.fromISO("9999-12-31");

type RemoveUnderscoreFirstLetter<S extends string> = S extends `${infer FirstLetter}${infer U}`
  ? `${FirstLetter extends "_" ? U : `${FirstLetter}${U}`}`
  : S;

type CamelToSnakeCase<S extends string> = S extends `${infer T}${infer U}`
  ? `${T extends Capitalize<T> ? "_" : ""}${RemoveUnderscoreFirstLetter<
      Lowercase<T>
    >}${CamelToSnakeCase<U>}`
  : S;

/**
 * Converts a camelCase string to snake_case at runtime, preserving the type.
 * e.g. "companyPaymentType" -> "company_payment_type"
 */
export function camelToSnakeCase<S extends string>(str: S): CamelToSnakeCase<S> {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`) as CamelToSnakeCase<S>;
}

export type KeysToSnakeCase<T extends Record<string, unknown>> = {
  [K in keyof T as CamelToSnakeCase<string & K>]: T[K] extends Array<infer U>
    ? U extends Record<string, unknown>
      ? Array<KeysToSnakeCase<U>>
      : Array<U>
    : T[K] extends Record<string, unknown>
      ? KeysToSnakeCase<T[K]>
      : T[K];
};

export function formatAmount(
  amount: number | string | null | undefined,
  currency?: string | null,
): string {
  if (amount === null || amount === undefined) return EM_DASH;
  if (typeof amount === "string") amount = parseInt(amount);

  const formatted = NUMBER_FORMAT.format(amount);

  if (!currency) {
    return formatted;
  } else if (isNaN(amount)) {
    return EM_DASH;
  }
  return `${formatted} ${currency}`;
}

/** The standard date format, e.g. "1 Sept, 2026". */
export const DATE_FORMAT = "d MMM, yyyy";
/** The standard date and time format, to the second, e.g. "1 Sept 2026, 09:00:00". */
export const DATE_TIME_FORMAT = "d MMM yyyy, HH:mm:ss";

/**
 * Formats date object to string by day, month name then full year
 * @param date
 * @returns
 */
export function formatDate(
  date: Date | null | undefined,
  locale = "en-GB",
  format = DATE_FORMAT,
): string {
  if (date === null || date === undefined) return EM_DASH;

  return DateTime.fromJSDate(date).setLocale(locale).toFormat(format);
}

/** Formats a date and time in the standard format, in the viewer's time zone. */
export function formatDateTime(date: Date | string | null | undefined): string {
  if (date === null || date === undefined) return EM_DASH;

  return formatDate(new Date(date), "en-GB", DATE_TIME_FORMAT);
}

export function isOpenEndedDate(date: Date): boolean {
  return OPEN_ENDED_DATE.equals(DateTime.fromJSDate(date));
}

export function formatDateOrOpenEnded(
  date?: Date | string | null,
  openEndedMessage?: string,
): string | undefined {
  if (!date) {
    return undefined;
  }

  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (isOpenEndedDate(dateObj)) {
    return openEndedMessage ?? "open-ended";
  }
  return formatDate(dateObj);
}

export function formatShortAmount(
  amount: number | string | null | undefined,
  currency?: string | null,
): string {
  if (amount === null || amount === undefined) return EM_DASH;
  if (typeof amount === "string") amount = parseInt(amount);

  const formatted = NUMBER_FORMAT_COMPACT.format(amount);

  if (isNaN(amount)) {
    return EM_DASH;
  } else if (!currency) {
    return formatted;
  }
  return `${formatted} ${currency}`;
}

export function getAddress(
  country?: string,
  address?: string,
  town?: string,
  postalCode?: string,
): string {
  const parts = [];
  if (address) {
    parts.push(address);
  }
  if (postalCode || town) {
    if (!postalCode) {
      parts.push(town);
    } else if (!town) {
      parts.push(postalCode);
    } else {
      parts.push(`${postalCode} ${town}`);
    }
  }
  if (country) {
    parts.push(country);
  }
  return parts.join(", ");
}

export function getCSRFToken(): string | undefined {
  const elems = document.getElementsByName("csrf-token");
  if (elems.length === 0) {
    return undefined;
  }
  const elem = elems.item(0);
  if (elem instanceof HTMLMetaElement) {
    return elem.content;
  }
  return undefined;
}

export function isDefined<T>(x: T): x is Exclude<T, undefined | null> {
  return x !== null && x !== undefined;
}

/**
 * Deeply checks not only if an object is defined, but if any of
 * its properties are defined as well
 * @param obj
 * @returns
 */
export function isEmptyDeep<T>(obj: T): boolean {
  // Check if the object itself is undefined or null
  if (!isDefined(obj)) {
    return true;
  }

  // Check if it's an object (this includes arrays and excludes functions)
  if (typeof obj !== "object") {
    return false;
  }

  // Special case for empty arrays
  if (Array.isArray(obj) && obj.length === 0) {
    return true;
  }

  // Check if every property of the object is undefined or null
  for (const key in obj) {
    if (Object.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value !== undefined && value !== null) {
        // Special case for arrays - check if they are not empty
        if (Array.isArray(value) && value.length === 0) {
          continue;
        }
        return false;
      }
    }
  }

  // If we reached this point, the object is either empty or all properties are undefined or null
  return true;
}

type ArrayDiff<T extends string> = {
  added: T[];
  removed: T[];
};

/**
 * Compares two arrays and returns the differences between them using lodash.
 *
 * @template T - String type of array elements
 * @param {readonly T[]} prev - The previous/original array
 * @param {readonly T[]} next - The new/updated array
 * @returns {ArrayDiff<T>} Object containing arrays of added and removed elements
 *
 * @example
 * const prev = ['a', 'b', 'c'];
 * const next = ['b', 'c', 'd'];
 * const diff = diffArray(prev, next);
 * // Returns: { added: ['d'], removed: ['a'] }
 */
export function diffArray<T extends string>(prev: readonly T[], next: readonly T[]): ArrayDiff<T> {
  return {
    added: difference(next, prev),
    removed: difference(prev, next),
  };
}

export function pluralise(
  count: number,
  noun: string,
  formatNumber?: boolean,
  adjective?: string,
): string {
  if (count != 1) {
    noun = `${noun}s`;
  }
  const formattedCount = formatNumber ? NUMBER_FORMAT.format(count) : count;
  return adjective ? `${formattedCount} ${adjective} ${noun}` : `${formattedCount} ${noun}`;
}

export function irregularPlural(count: number, singular: string, plural: string): string {
  return count !== 1 ? plural : singular;
}

/**
 * With input @param date output the date in a yyyy-mm-dd format
 * @param date
 * @returns
 */
export function getFormattedDate(date: Date): string {
  return date.toISOString().split("T")[0];
}

/**
 * Resets a date to the start of day midnight. Essentially strips time from the JS date object
 * @param date
 * @returns
 */
export function startOfDay(date: Date): Date {
  date.setHours(0, 0, 0, 0); // Sets hours, minutes, seconds, and milliseconds to zero
  return date;
}

export function linkedinPeopleSearchURL(...keywords: (string | null)[]): string {
  const query = keywords.filter(isDefined).join(" ").replace(" ", "%20");
  return `https://www.linkedin.com/search/results/people/?keywords=${query}`;
}

export function stopEventPropagation<E extends React.MouseEvent | React.TouchEvent>(
  handler?: (e: E) => void,
): (e: E) => void {
  return (e: E) => {
    e.stopPropagation();
    if (handler) handler(e);
  };
}

export const defaultStopEventPropagation = stopEventPropagation();

export function removeUndefined(r: Record<string, unknown>): void {
  for (const key of Object.keys(r)) {
    if (r[key] === undefined) {
      delete r[key];
    }
  }
}

export function downloadFile(blob: Blob, fileName: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  // Some browsers require the link to be added to the document
  document.body.append(link);
  link.click(); // this actually triggers the download
  link.remove();
  URL.revokeObjectURL(link.href);
}

/**
 * Generate a filename with proper extension
 * @param providedName - User provided filename (optional)
 * @param extension - File extension (e.g. "docx", "pdf")
 * @param fallbackPrefix - Fallback prefix if no name provided (default: "document")
 * @returns Properly formatted filename with extension
 */
export function generateFilename(
  providedName: string | undefined,
  extension: string,
  fallbackPrefix: string = "document",
): string {
  if (providedName) {
    // Remove any existing extension and add the correct one
    const nameWithoutExtension = providedName.replace(/\.[^/.]+$/, "");
    return sanitizeFilename(`${nameWithoutExtension}.${extension}`);
  }

  // Fallback to timestamp-based name
  const timestamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  return sanitizeFilename(`${fallbackPrefix}_${timestamp}.${extension}`);
}

/**
 * Provide a link to download the file. Avoids pesky issues with popup blocker
 * @param href
 * @param fileName
 */
export function downloadFileFromLink(href: string, fileName?: string): void {
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName ?? "";
  link.style.display = "none";
  document.body.append(link);
  link.click();
  link.remove();
}

/**
 * Downloads a file from a remote URL using fetch and triggers a browser download.
 * This method avoids popup blockers and navigation flashes by fetching the file as a Blob
 * and creating a temporary object URL for download.
 *
 * ⚠️ Important: This function should only be used with S3 buckets (or other remote storage)
 * that have their CORS policy correctly configured to allow requests from the frontend origin.
 * If CORS is not set, the browser will block the fetch request and the download will fail.
 *
 * @param url - The remote file URL (e.g. S3 presigned URL)
 * @param fileName - The desired filename for the downloaded file
 * @returns Promise<void>
 */
export async function downloadFileViaFetch(url: string, fileName?: string): Promise<void> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      toast.error("Download failed. Please try again later.");
      return;
    }
    const blob = await response.blob();

    let finalFileName = fileName;
    // Try to extract filename from Content-Disposition header if not provided
    if (!finalFileName) {
      const disposition = response.headers.get("Content-Disposition");
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          finalFileName = match[1];
        }
      }

      if (!finalFileName) {
        finalFileName = "stotles-download";
      }
    }
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = finalFileName;
    link.style.display = "none";
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  } catch (error) {
    toast.error("Download failed. Please try again later.");
    return;
  }
}

/**
 * Sanitizes a string for safe use as a filename by replacing or removing
 * characters that are invalid in most file systems, normalizing whitespace
 * and truncating if needed.
 */
export function sanitizeFilename(title: string): string {
  return title
    .replace(/[/\\?%*:|"<>]/g, "-") // replace invalid characters
    .replace(/^_+|_+$/g, "") // trim leading/trailing underscores
    .slice(0, 100); // trim to max 100 characters
}

/**
 * Creates a `setState`-like function that lets you set a specific field on a state object.
 * Use this when your state is an object which fields are often updated separately.
 * Takes same parameters as `setState` (value or update function) and doesn't trigger
 * an update if the value of the field didn't change.
 *
 * Example:
 *
 * const [state, setState] = React.useState<{ fieldA: number, fieldB: SomeType }>(...);
 * const updateFieldA = React.useMemo(() => createSetNestedState('fieldA', setState));
 *
 * // somewhere else in code
 * updateFieldA(5);
 *
 * @param field - the key of a field in `State`
 * @param setState - usually the `set*` function returned by `useState` hook
 */
export function createSetNestedState<State, Key extends keyof State>(
  field: Key,
  setState: React.Dispatch<React.SetStateAction<State>>,
): React.Dispatch<React.SetStateAction<State[Key]>> {
  return (valueOrUpdateFn) => {
    return setState((oldState) => {
      const oldFieldState = oldState[field];
      let newState: State[Key];
      if (typeof valueOrUpdateFn === "function") {
        // We can't fully assert that `value` in `valueOrUpdateFn` won't be a function
        // so TS can't 100% check that this is a correct call.
        newState = (valueOrUpdateFn as (oldState: State[Key]) => State[Key])(oldFieldState);
      } else {
        newState = valueOrUpdateFn;
      }
      if (newState === oldFieldState) {
        return oldState;
      } else {
        return {
          ...oldState,
          [field]: newState,
        };
      }
    });
  };
}

export const capitalize = <T extends string>(s: T): Capitalize<T> => {
  return (s.charAt(0).toUpperCase() + s.slice(1)) as Capitalize<T>;
};

export const toTitleCase = (s: string) => {
  return s
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export function convertCamelCaseToSpaceSeparatedWords(field: string) {
  const words = field.replace(/([a-z])([A-Z])/g, "$1 $2");
  return words;
}

export function mapIterable<T, U>(iterable: IterableIterator<T>, fn: (item: T) => U): U[] {
  const result: U[] = [];
  for (const item of iterable) {
    result.push(fn(item));
  }
  return result;
}

export function filterIterable<T>(iterable: IterableIterator<T>, fn: (item: T) => boolean): T[] {
  return Array.from(iterable).filter(fn);
}

export function assertDefined<T>(value: T | null | undefined): asserts value is T {
  if (value == null) {
    throw new Error(`Fatal error: value ${value} must not be null/undefined.`);
  }
}

export function joinToEnglishList(words: string[], exclusiveList?: boolean): string {
  const connectiveWord = exclusiveList ? "or" : "and";
  switch (words.length) {
    case 0:
      return "";
    case 1:
      return words[0];
    case 2:
      return words.join(` ${connectiveWord} `);
    default:
      return words.slice(0, -1).join(", ") + ` ${connectiveWord} ${words[words.length - 1]}`;
  }
}

const ELLIPSIS = "…";

export function truncate(str: string, length: number): string {
  if (str.length > length) {
    return str.slice(0, length) + ELLIPSIS;
  } else {
    return str;
  }
}

export function assert<T>(value: T | null | undefined): asserts value is T {
  if (value === null || value === undefined) {
    throw new Error("Expected value to be defined.");
  }
}

export function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
}

export function formatAddress(
  address: ReferenceOrganisation_Address,
  town: string | undefined | null,
): string {
  return [address.address, address.address2, town, address.countryCode]
    .filter(isDefined)
    .filter((field) => field.trim() !== "")
    .join(", ");
}

export function* deduplicateByKey<T>(
  iterable: Iterable<T>,
  extractKey: (element: T) => string,
): IterableIterator<T> {
  const seenKeys = new Set<string>();

  for (const element of iterable) {
    const elementKey = extractKey(element);
    const isDuplicate = seenKeys.has(elementKey);

    if (!isDuplicate) {
      yield element;
    }

    seenKeys.add(elementKey);
  }
}

/**
 * Removes duplicate items from an array
 * @param items
 * @returns
 */
export function simpleArrayDedupe(items: string[]): string[] {
  return Array.from(new Set(items));
}

/**
 * Checks if all values in the given object are `undefined` or the object itself is undefined / null.
 *
 * @param {object | undefined | null} obj - The object to check.
 * @returns {boolean} `true` if all values in the object are `undefined`, or if the object itself is `undefined` / null; otherwise, `false`.
 */
export function isObjEmpty(obj: object | undefined | null): boolean {
  if (!obj || obj === null) return true;
  return Object.values(obj).every((value) => value === undefined);
}

/**
 * Checks if every element of array a is in array b
 * @param a
 * @param b
 * @returns {boolean} `true` if all elements of array a are in array b; otherwise, `false`.
 */
export function isArrayInArray(a: string[], b: string[]): boolean {
  const setB = new Set(b);
  return a.every((item) => setB.has(item));
}

/**
 * Converts a camelCase string to a sentence with spaces between words and capitalizes the first letter of first word
 *
 * @param camelCaseStr - The camelCase string to convert
 * @returns {string} The sentence with spaces between words and the first letter of the first word capitalized
 */
export function camelToSentence(camelCaseStr: string): string {
  // Split the camelCase string into words using a regular expression
  const words = camelCaseStr.match(/[A-Z]?[a-z]+|[A-Z]+(?=[A-Z]|$)/g);

  if (!words) {
    return camelCaseStr;
  }

  const sentence = words.join(" ").toLowerCase();
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

/**
 * In an object of keys -> string arrays, finds the key that contains the given string
 *
 * @param value - The string to find in one of the keys of the object
 * @param object - The object of keys with string arrays e.g { key1: ['value1', 'value2'], key2: ['value3'] }
 * @returns {string} The key that contains the given string
 */
export function findKeybyValue(value: string | undefined, object: Record<string, string[]>) {
  if (!value) return undefined;

  return Object.keys(object).find((key) => object[key].includes(value));
}

export function completeUnclosedQuotes(str: string): string {
  let quoteFound = false; // To track if we're inside an unclosed quote

  // Traverse the string and track open quotes
  for (let i = 0; i < str.length; i++) {
    const char = str[i];

    // Check if the character is a double quote
    if (char === '"') {
      if (quoteFound) {
        // Matching closing quote found, so we're no longer inside a quote
        quoteFound = false;
      } else {
        // Check if the quote is at the start of a word (i.e., followed by a letter/number)
        const nextChar = str[i + 1];
        if (/[a-zA-Z0-9]/.test(nextChar)) {
          // Opening quote found, so we are now inside a quote
          quoteFound = true;
        }
      }
    }
  }

  // If quoteFound is still true - the string has an unclosed quote. So we must close it.
  if (quoteFound) {
    str += '"'; // Add the corresponding closing double quote
  }

  return str;
}

export function formatDataType(dataType: string | undefined) {
  if (dataType === undefined) {
    return undefined;
  }

  if (dataType.includes("buyer")) {
    return "BUYERS";
  }
  if (dataType.includes("supplier")) {
    return "SUPPLIERS";
  }
  if (dataType.includes("spend")) {
    return "SPEND";
  }
  if (dataType.includes("contact")) {
    return "CONTACTS";
  }
  if (dataType.includes("framework")) {
    return "FRAMEWORKS";
  }
  if (dataType.includes("document")) {
    return "DOCUMENTS";
  }
  // Upcoming expiries view, no easy way of doing this, this will works at least some of the time
  // it should catch the upgrade button being clicked on the nav bar on this page
  if (dataType.includes("c735a82a-4fea-4a97-84a8-7f9638e64c25")) {
    return "AWARDS";
  }
  return undefined;
}

export const LINK_REGEX =
  /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i;

export function normaliseMarkdownText(text: string): string {
  return (
    text
      //  Convert literal '\n' into real newlines
      .replace(/\\n/g, "\n")

      // If bullets appear inline (after a colon or other text), start a markdown list
      .replace(/:\s*•\s*/g, ":\n\n- ")

      // Any remaining inline bullets become new list items
      .replace(/\s*•\s*/g, "\n- ")

      //  Normalize any excess blank lines (optional)
      .replace(/\n{3,}/g, "\n\n")
  );
}

export function applyUpdatedWithValueDelta<T extends { id: string; value?: number | null }>(
  items: T[],
  ids: string[],
  updater: (item: T) => T,
): { results: T[]; valueDelta: number } {
  let valueDelta = 0;

  const results = items.map((item) => {
    if (!ids.includes(item.id)) return item;
    const updated = updater(item);
    valueDelta += (updated.value ?? 0) - (item.value ?? 0);
    return updated;
  });

  return { results, valueDelta };
}

/** Drop trailing slashes so "/bids/" and "/bids" compare equal; the root (or "") becomes "/". */
export function removeTrailingSlash(pathname: string): string {
  return pathname.replace(/\/+$/, "") || "/";
}

/** Strip `prefix` off the path, preferring reactive router state over
 *  window.location (which memory-history tests don't update). */
export function stripPathPrefix(routerPathname: string, prefix: string, fallback = "/"): string {
  if (routerPathname.startsWith(prefix)) return routerPathname.slice(prefix.length) || fallback;
  const win = window.location.pathname;
  if (win.startsWith(prefix)) return win.slice(prefix.length) || fallback;
  return fallback;
}
